import { Link } from "react-router-dom";
import { AlertTriangle, Banknote, Boxes, CalendarRange, CheckCircle2, Clock, DoorOpen, FileText, HandCoins, Info, PackageCheck, ShoppingCart, Warehouse } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import usePoll from "@/hooks/usePoll";
import { Kpi, Loading, Money, PageHeader, ErrorBox } from "@/components/common";
import { money, dt } from "@/lib/api";

const ACTION_AR = { sale_created: "تسجيل بيع", payment_recorded: "تسجيل دفعة", permit_issued: "إصدار تصريح", permit_issued_exception: "تصريح باستثناء",
  sheep_delivered: "تسليم داخل الحظيرة", gate_exit_confirmed: "خروج من البوابة", sale_cancelled: "إلغاء بيع", permit_cancelled: "إلغاء تصريح" };

const GroupTable = ({ title, rows, testid }) => (
  <div className="k-card overflow-hidden" data-testid={testid}>
    <div className="px-5 py-4 font-display font-bold">{title}</div>
    <div className="overflow-x-auto"><table className="k-table">
      <thead><tr><th>البيان</th><th>العدد</th><th>القيمة</th><th>المحصّل</th><th>المتبقي</th></tr></thead>
      <tbody>{rows.map((r) => <tr key={r.key}><td className="font-bold">{r.key}</td><td className="num">{r.count}</td><td><Money v={r.value} /></td><td><Money v={r.collected} className="text-emerald-700" /></td><td><Money v={r.remaining} className="text-rose-700" /></td></tr>)}
        {!rows.length && <tr><td colSpan={5} className="text-center text-slate-500">لا توجد مبيعات بعد</td></tr>}</tbody>
    </table></div>
  </div>
);

export default function Dashboard() {
  const { data: d, error } = usePoll("/dashboard", null, 15000);
  if (error) return <ErrorBox text={error} />;
  if (!d) return <Loading />;
  const inv = d.inventory;
  return (
    <div className="space-y-6">
      <PageHeader title="لوحة التحكم" subtitle="أرقام محدّثة تلقائيًا من قاعدة البيانات المركزية">
        <div className="k-card px-4 py-2 text-sm flex items-center gap-2" data-testid="delivery-period-chip">
          <CalendarRange className="w-4 h-4 text-[#B45309]" /> فترة التسليم: {d.delivery_start ? `${d.delivery_start} ← ${d.delivery_end}` : "غير محددة"}
        </div>
      </PageHeader>
      {d.alerts.length > 0 && (
        <div className="grid gap-2" data-testid="dashboard-alerts">
          {d.alerts.map((a, i) => (
            <div key={i} className={`rounded-xl border px-4 py-3 text-sm font-bold flex items-center gap-2 ${a.level === "danger" ? "bg-rose-50 border-rose-300 text-rose-800" : a.level === "warning" ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-sky-50 border-sky-300 text-sky-800"}`}>
              {a.level === "info" ? <Info className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />} {a.text}
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-purchased" label="الأغنام المشتراة" value={inv.purchased} Icon={Boxes} sub={`مستبعدة: ${inv.excluded}`} />
        <Kpi testid="kpi-available" label="متاحة للبيع (حسب المسجل)" value={inv.available_for_sale} Icon={Warehouse} tone="blue" sub={`داخل الحظيرة فعليًا: ${inv.in_barn}`} />
        <Kpi testid="kpi-sales" label="عمليات البيع / الأغنام المباعة" value={d.sales_count} Icon={ShoppingCart} tone="ochre" sub={`ملغاة: ${inv.cancelled_sales}`} />
        <Kpi testid="kpi-paid-split" label="مدفوع كاملًا / جزئي / غير مدفوع" value={`${d.paid_full} / ${d.paid_partial} / ${d.unpaid}`} Icon={CheckCircle2} />
        <Kpi testid="kpi-sales-value" label="قيمة المبيعات" value={money(d.sales_value)} Icon={Banknote} tone="slate" sub="ليست كلها نقدًا في الصندوق" />
        <Kpi testid="kpi-collected" label="المبالغ المستلمة فعليًا" value={money(d.collected)} Icon={HandCoins} />
        <Kpi testid="kpi-debts" label="الديون والأرصدة المتبقية" value={money(d.debts)} Icon={Clock} tone="rose" />
        <Kpi testid="kpi-permits" label="تصاريح صادرة" value={d.permits_issued} Icon={FileText} tone="blue" sub={`بدون تصريح: ${d.no_permit}`} />
        <Kpi testid="kpi-delivered" label="سُلّمت داخل الحظيرة" value={d.delivered} Icon={PackageCheck} tone="blue" />
        <Kpi testid="kpi-exited" label="خرجت فعليًا من البوابة" value={d.exited} Icon={DoorOpen} tone="slate" />
      </div>
      <div className="k-card p-5">
        <div className="font-display font-bold mb-4">المبيعات اليومية (آخر 14 يومًا)</div>
        <div className="h-64" dir="ltr">
          <ResponsiveContainer><BarChart data={d.by_day}><CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis dataKey="key" fontSize={11} /><YAxis allowDecimals={false} fontSize={11} />
            <Tooltip formatter={(v, n) => (n === "count" ? [v, "عدد"] : [money(v), "القيمة"])} /><Bar dataKey="count" fill="#1A4D2E" radius={[6, 6, 0, 0]} /></BarChart></ResponsiveContainer>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <GroupTable title="المبيعات حسب القسم" rows={d.by_section} testid="by-section-table" />
        <GroupTable title="المبيعات حسب العامل المسؤول عن الاختيار" rows={d.by_worker} testid="by-worker-table" />
      </div>
      <div className="k-card p-5" data-testid="recent-ops">
        <div className="font-display font-bold mb-3">آخر العمليات</div>
        <ul className="divide-y divide-border">
          {d.recent.map((r) => (
            <li key={r.id} className="py-2.5 flex flex-wrap items-center gap-2 text-sm">
              <span className="font-bold text-[#1A4D2E]">{ACTION_AR[r.action] || r.action}</span>
              <span className="text-slate-600">{r.after?.number} {r.after?.ear_tag ? `– كبش ${r.after.ear_tag}` : ""} {r.after?.amount ? `– ${money(r.after.amount)}` : ""}</span>
              <span className="text-slate-400 mr-auto">{r.user_name} · {dt(r.at)}</span>
            </li>
          ))}
          {!d.recent.length && <li className="py-6 text-center text-slate-500">لا توجد عمليات بعد – <Link className="underline" to="/sales/new">سجّل أول بيع</Link></li>}
        </ul>
      </div>
    </div>
  );
}
